import { useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { api, formatApiError, formatData } from "@/lib/api";
import { PageHeader } from "@/components/dashboard/PageHeader";

const EMPTY = { nome: "", cognome: "", data_nascita: "", codice_fiscale: "" };

export function SearchPanel() {
  const [form, setForm] = useState(EMPTY);
  const [finalita, setFinalita] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    if (!finalita) return setError("Devi dichiarare la finalità della ricerca.");
    setLoading(true);
    try {
      const { data } = await api.post("/search", { ...form, data_nascita: form.data_nascita || null, codice_fiscale: form.codice_fiscale || null, finalita_dichiarata: true });
      setResult(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Ricerca Anagrafica Ufficiale" title="Verifica un candidato" text="La ricerca interroga esclusivamente le convalide di sfratto verificate dal nostro team. Ogni consultazione viene registrata." />
      <div className="grid gap-8 xl:grid-cols-[1fr_1fr]">
        <form onSubmit={submit} className="surface grid gap-4 p-6 sm:grid-cols-2 sm:p-8" data-testid="search-form">
          <div><label className="field-label">Nome *</label><input data-testid="search-input-nome" required className="field-input" value={form.nome} onChange={set("nome")} /></div>
          <div><label className="field-label">Cognome *</label><input data-testid="search-input-cognome" required className="field-input" value={form.cognome} onChange={set("cognome")} /></div>
          <div><label className="field-label">Data di nascita</label><input data-testid="search-input-data-nascita" type="date" className="field-input" value={form.data_nascita} onChange={set("data_nascita")} /></div>
          <div><label className="field-label">Codice fiscale</label><input data-testid="search-input-cf" maxLength={16} className="field-input uppercase" value={form.codice_fiscale} onChange={set("codice_fiscale")} /></div>
          <label className="flex items-start gap-3 rounded-xl bg-slate-50 p-4 text-xs leading-relaxed text-slate-600 sm:col-span-2">
            <Checkbox data-testid="search-checkbox-finalita" checked={finalita} onCheckedChange={(v) => setFinalita(!!v)} className="mt-0.5" />
            Dichiaro che la ricerca è effettuata esclusivamente per valutare una candidatura di locazione di un mio immobile, nel rispetto del Regolamento UE 2016/679.
          </label>
          {error && <p data-testid="search-error" className="text-sm text-[#dc2626] sm:col-span-2">{error}</p>}
          <button data-testid="search-button-submit" type="submit" disabled={loading} className="btn-accent sm:col-span-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}Verifica nel Database
          </button>
        </form>
        <div>
          {!result && <div className="flex h-full min-h-[240px] items-center justify-center rounded-2xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">Il risultato della verifica comparirà qui.</div>}
          {result?.trovato && (
            <div data-testid="search-result" data-result="danger" className="fade-up rounded-2xl border border-red-200 bg-red-50 p-6 text-red-900">
              <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-5 w-5" />Segnalazione Presente</p>
              <p className="mt-2 text-sm">Risultano {result.risultati.length} provvedimenti esecutivi di Convalida di Sfratto verificati.</p>
              <ul className="mt-4 space-y-2">
                {result.risultati.map((r) => (
                  <li key={r.id} className="rounded-xl bg-white/70 px-4 py-3 text-sm">
                    <p className="font-semibold">{r.tribunale}</p>
                    <p className="text-xs text-red-800">R.G. {r.numero_rg} · Convalida del {formatData(r.data_convalida)} · Nato/a il {formatData(r.data_nascita)}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result && !result.trovato && (
            <div data-testid="search-result" data-result="ok" className="fade-up rounded-2xl border border-green-200 bg-green-50 p-6 text-green-900">
              <p className="flex items-center gap-2 font-semibold"><CheckCircle2 className="h-5 w-5" />Nessun Precedente</p>
              <p className="mt-2 text-sm">Nessuno sfratto certificato in archivio per questo soggetto.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
